import { signalingWsUrl } from '../config';
import { Emitter } from '../util/emitter';
import type { PeerRole } from './types';

/** Signaling payloads relayed peer-to-peer through the Worker. */
export type SignalPayload =
  | { type: 'offer'; sdp: string }
  | { type: 'answer'; sdp: string }
  | { type: 'candidate'; candidate: RTCIceCandidateInit }
  | { type: 'resume'; missingChunks: number[] };

interface ServerJoined {
  type: 'joined';
  role: PeerRole;
  peerPresent: boolean;
  expiresAt: string;
}

type SignalingEvents = {
  open: void;
  joined: ServerJoined;
  'peer-joined': { role: PeerRole };
  'peer-left': { role: PeerRole };
  signal: SignalPayload;
  error: { code: string; message: string };
  /** Socket closed. `willRetry` indicates an automatic reconnect is scheduled. */
  close: { code: number; reason: string; willRetry: boolean };
};

const RELAYED = new Set(['offer', 'answer', 'candidate', 'resume']);

/**
 * SignalingClient — thin WebSocket client for the signaling Worker.
 *
 * Responsibilities: connect to a room as sender/receiver, surface presence
 * events, relay SDP/ICE, and auto-reconnect with backoff so the resume flow can
 * recover dropped connections. It carries NO file data.
 */
export class SignalingClient extends Emitter<SignalingEvents> {
  private ws: WebSocket | null = null;
  private closedByUser = false;
  private retries = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly roomId: string,
    private readonly role: PeerRole,
  ) {
    super();
  }

  connect(): void {
    this.closedByUser = false;
    this.open();
  }

  private open(): void {
    const ws = new WebSocket(signalingWsUrl(this.roomId, this.role));
    this.ws = ws;

    ws.onopen = () => {
      this.retries = 0;
      this.emit('open', undefined);
    };

    ws.onmessage = (ev) => {
      if (typeof ev.data !== 'string') return; // signaling is JSON only
      let msg: unknown;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (typeof msg !== 'object' || msg === null) return;
      const type = (msg as { type?: unknown }).type;
      if (typeof type !== 'string') return;

      if (RELAYED.has(type)) {
        this.emit('signal', msg as SignalPayload);
      } else if (type === 'joined') {
        this.emit('joined', msg as ServerJoined);
      } else if (type === 'peer-joined') {
        this.emit('peer-joined', { role: (msg as { role: PeerRole }).role });
      } else if (type === 'peer-left') {
        this.emit('peer-left', { role: (msg as { role: PeerRole }).role });
      } else if (type === 'error') {
        this.emit('error', msg as { code: string; message: string });
      }
    };

    ws.onclose = (ev) => {
      this.ws = null;
      // 4000 (expired) / 4001 (replaced) are terminal — don't reconnect.
      const terminal = this.closedByUser || ev.code === 4000 || ev.code === 4001;
      const willRetry = !terminal && this.retries < 8;
      this.emit('close', { code: ev.code, reason: ev.reason, willRetry });
      if (willRetry) this.scheduleReconnect();
    };

    ws.onerror = () => {
      // The close handler drives reconnect; nothing extra needed here.
    };
  }

  private scheduleReconnect(): void {
    const delay = Math.min(1000 * 2 ** this.retries, 15_000);
    this.retries++;
    this.retryTimer = setTimeout(() => this.open(), delay);
  }

  /** Send a signaling payload to the other peer. */
  send(payload: SignalPayload): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  close(): void {
    this.closedByUser = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.ws?.close(1000, 'client closed');
    this.ws = null;
    this.removeAll();
  }
}
